import React from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  ArrowRightStartOnRectangleIcon,
  ArrowTrendingUpIcon,
  BeakerIcon,
  BriefcaseIcon,
  BuildingOffice2Icon,
  ChartBarSquareIcon,
  ClipboardDocumentListIcon,
  Cog6ToothIcon,
  CurrencyDollarIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  HeartIcon,
  KeyIcon,
} from '@heroicons/react/24/outline';
import { apiService } from '../services/api';

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

interface NavGroup {
  title: string;
  items: NavItem[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    title: 'Analytics',
    items: [
      { to: '/admin/overview', label: 'Overview', icon: ChartBarSquareIcon },
      { to: '/admin/usage', label: 'Usage', icon: ArrowTrendingUpIcon },
      { to: '/admin/converter-demo', label: 'Converter Demo', icon: BeakerIcon },
    ],
  },
  {
    title: 'Resources',
    items: [
      { to: '/admin/instances', label: 'Instances', icon: BuildingOffice2Icon },
      { to: '/admin/jobs', label: 'Jobs', icon: BriefcaseIcon },
      { to: '/admin/api-keys', label: 'API Keys', icon: KeyIcon },
    ],
  },
  {
    title: 'Operations',
    items: [
      { to: '/admin/errors', label: 'Errors', icon: ExclamationTriangleIcon },
      { to: '/admin/audit-log', label: 'Audit Log', icon: ClipboardDocumentListIcon },
      { to: '/admin/contact-requests', label: 'Contact Requests', icon: EnvelopeIcon },
      { to: '/admin/health', label: 'Health', icon: HeartIcon },
    ],
  },
  {
    title: 'Configuration',
    items: [
      { to: '/admin/features', label: 'Features', icon: Cog6ToothIcon },
      { to: '/admin/pricing', label: 'Pricing', icon: CurrencyDollarIcon },
    ],
  },
];

// Admin console shell: fixed sidebar navigation with the admin identity in
// its header, and a routed content area (see /admin/* routes in App.tsx).
const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const adminEmail = localStorage.getItem('admin_email');

  const handleLogout = async () => {
    try {
      await apiService.adminLogout();
    } catch (error) {
      // Log error but continue with logout process
      console.error('Failed to call logout API:', error);
    }
    try {
      localStorage.removeItem('admin_token');
    } catch (error) {
      console.error('Failed to remove admin token from localStorage:', error);
    }
    // Keep admin_email for pre-filling on next login
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-body-bg flex">
      <aside className="w-60 shrink-0 border-r border-gray-700 flex flex-col sticky top-0 h-screen">
        <div className="px-4 py-4 border-b border-gray-700">
          <h1 className="text-lg font-bold text-heading">Admin Dashboard</h1>
          <div className="mt-1 flex items-center justify-between gap-1">
            <span className="text-xs text-secondary truncate min-w-0" title={adminEmail ?? undefined}>
              {adminEmail}
            </span>
            <button
              onClick={handleLogout}
              className="btn-icon shrink-0 -my-1 -mr-2"
              title="Logout"
              aria-label="Logout"
            >
              <ArrowRightStartOnRectangleIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-6">
          {NAV_GROUPS.map((group) => (
            <div key={group.title}>
              <div className="px-2 mb-2 text-xs font-medium text-secondary uppercase tracking-wider">
                {group.title}
              </div>
              <div className="space-y-1">
                {group.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      className={({ isActive }) =>
                        `${isActive ? 'nav-link-active' : 'btn-subtle'} w-full`
                      }
                    >
                      <Icon className="w-4 h-4 shrink-0" />
                      {item.label}
                    </NavLink>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>
      </aside>

      <main className="flex-1 min-w-0 px-6 py-6 overflow-x-hidden">
        <Outlet />
      </main>
    </div>
  );
};

export default AdminDashboard;
